import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Crown, ChevronDown } from "lucide-react";
import { scrollTo } from "@/lib/lenis";
import { IMAGES } from "@/lib/site";

function GoldParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: 70 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.8 + 0.4,
      speed: Math.random() * 0.00035 + 0.0001,
      phase: Math.random() * Math.PI * 2,
      drift: (Math.random() - 0.5) * 0.0002,
    }));

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.y -= p.speed;
        p.x += p.drift;
        if (p.y < -0.02) { p.y = 1.02; p.x = Math.random(); }
        if (p.x < -0.02) p.x = 1.02;
        if (p.x > 1.02) p.x = -0.02;
        const twinkle = 0.35 + 0.65 * Math.abs(Math.sin(t / 900 + p.phase));
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(240, 199, 94, ${twinkle * 0.8})`;
        ctx.shadowColor = "rgba(212, 175, 55, 0.9)";
        ctx.shadowBlur = 6;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" data-testid="hero-particles" />;
}

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, []);

  return (
    <section id="home" ref={ref} className="relative min-h-[100svh] overflow-hidden flex items-center" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          src="/assets/hero.mp4?v=ai-runway"
          poster={IMAGES.hero}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          data-testid="hero-video"
        />
      </motion.div>
      <div className="absolute inset-0 bg-royal-950/72" />
      <div className="absolute inset-0 bg-gradient-to-r from-royal-950 via-royal-950/60 to-transparent" />
      <div className="absolute inset-0 spotlight-top" />
      <div className="absolute inset-0 spotlight-fuchsia" />
      <GoldParticles />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <Crown className="text-gold" size={20} />
          <span className="tracking-[0.4em] uppercase text-xs sm:text-sm text-gold/90 font-medium" data-testid="hero-eyebrow">
            HA Treasure House Presents
          </span>
        </motion.div>

        <h1 className="font-display font-bold tracking-tight leading-[1.02] text-[13vw] sm:text-7xl lg:text-8xl max-w-5xl text-shadow-hero" data-testid="hero-headline">
          <MaskedLine delay={0.4}>CENTER OF</MaskedLine>
          <MaskedLine delay={0.55}>
            EXCELLENCE <span className="italic font-semibold text-cream/90">for</span>
          </MaskedLine>
          <MaskedLine delay={0.7}>
            <span className="gold-text-animated">GREAT TALENT</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9 }}
          className="mt-8 font-display italic text-xl sm:text-2xl text-gold-light"
          data-testid="hero-tagline"
        >
          Where Talent Meets the Spotlight
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          className="mt-4 max-w-xl text-muted leading-relaxed text-sm sm:text-base"
          data-testid="hero-description"
        >
          Discover your potential. Showcase your talent. Build your identity. Step into the spotlight with HA Treasure House.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("#register")}
            className="rounded-full bg-gold text-royal-950 px-9 py-4 font-semibold tracking-widest text-sm transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_35px_rgba(212,175,55,0.55)] hover:-translate-y-0.5"
            data-testid="hero-register-cta"
          >
            REGISTER NOW
          </button>
          <button
            onClick={() => scrollTo("#events")}
            className="rounded-full border border-gold/50 px-9 py-4 font-semibold tracking-widest text-sm text-gold transition-all duration-300 hover:bg-gold/10 hover:border-gold"
            data-testid="hero-explore-cta"
          >
            EXPLORE EVENTS
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/70 hover:text-gold transition-colors"
        aria-label="Scroll down"
        data-testid="hero-scroll-cue"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ChevronDown size={28} />
        </motion.div>
      </motion.button>
    </section>
  );
}
